import React, { useEffect, useState } from "react";
import { Spinner, Table } from "reactstrap";

import { resumoBaseline } from "services/bidFreteApi";
import { fmtBRL, fmtDataHora, fmtNumero, fmtPct } from "../regras";

// Aba Baseline (H3 PO1-2844): cobertura (NFs comparáveis), cobrado, custo extra
// cobrado (complementar, fora da economia), NFs sem CTe e a quebra por UF e
// por transportadora atual. Lê GET /baseline/resumo depois da etapa concluída.

function Kpi({ label, value, sub }) {
  return (
    <div className="lf-kpi">
      <div className="lf-kpi-label">{label}</div>
      <div className="lf-kpi-value">{value}</div>
      {sub ? <div className="lf-kpi-sub">{sub}</div> : null}
    </div>
  );
}

function n(v) {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

export default function AbaBaseline({ bid, empresaId }) {
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const concluida = Boolean(bid && bid.baselineEm);

  useEffect(() => {
    if (!concluida || !empresaId || !bid) return undefined;
    let ativo = true;
    setLoading(true);
    resumoBaseline(empresaId, bid.id)
      .then((r) => {
        if (ativo) setResumo(r);
      })
      .catch((err) => {
        if (!ativo) return;
        const data = (err.response && err.response.data) || {};
        setErro(data.error || "Não foi possível carregar a baseline.");
      })
      .finally(() => {
        if (ativo) setLoading(false);
      });
    return () => {
      ativo = false;
    };
  }, [concluida, empresaId, bid]);

  if (!concluida) return <div className="bf-placeholder">A baseline ainda não foi criada. Use o botão Criar baseline para congelar as NFs do escopo e tirar a foto do frete cobrado.</div>;
  if (loading && !resumo)
    return (
      <div className="lf-empty-state">
        <Spinner size="sm" /> Carregando a baseline...
      </div>
    );
  if (erro && !resumo) return <div className="lf-aviso lf-aviso-danger">{erro}</div>;

  const r = resumo || {};
  const qtdNf = r.qtdNf != null ? n(r.qtdNf) : n(bid.qtdNf);
  const comparaveis = r.qtdNfComparavel != null ? n(r.qtdNfComparavel) : n(bid.qtdNfComparavel);
  const semCte = r.qtdNfSemCte != null ? n(r.qtdNfSemCte) : n(bid.qtdNfSemCte);
  const cobrado = r.vrBaselineTotal != null ? n(r.vrBaselineTotal) : n(bid.vrBaselineTotal);
  const extra = r.vrComplementarTotal != null ? n(r.vrComplementarTotal) : n(bid.vrComplementarTotal);
  const cobertura = qtdNf > 0 ? comparaveis / qtdNf : null;

  return (
    <>
      <div className="lf-kpis">
        <Kpi label="NFs congeladas" value={fmtNumero(qtdNf)} sub={`foto em ${fmtDataHora(r.baselineEm || bid.baselineEm)}`} />
        <Kpi label="Cobertura (com CTe)" value={cobertura != null ? fmtPct(cobertura) : "—"} sub={`${fmtNumero(comparaveis)} comparáveis`} />
        <Kpi label="Sem CTe" value={fmtNumero(semCte)} sub="fora da economia" />
        <Kpi label="Frete cobrado" value={fmtBRL(cobrado)} sub="CTe normal ou substituto, rateado" />
        <Kpi label="Custo extra cobrado" value={fmtBRL(extra)} sub="complementar, à parte" />
        {r.qtdEmbarques != null || bid.qtdEmbarques != null ? <Kpi label="Embarques" value={fmtNumero(r.qtdEmbarques != null ? r.qtdEmbarques : bid.qtdEmbarques)} sub="CTe, romaneio ou NF" /> : null}
      </div>

      {cobertura !== null && cobertura < 0.5 ? <div className="lf-aviso lf-aviso-warning">Menos da metade das NFs tem CTe: a economia da simulação só é medida nas comparáveis.</div> : null}

      <div className="bf-split">
        <div className="lf-section">
          <div className="lf-section-title">Por UF de destino</div>
          {(r.porUf || []).length === 0 ? (
            <div className="lf-muted">Sem quebra por UF.</div>
          ) : (
            <Table className="bf-table" size="sm" responsive>
              <thead className="text-primary">
                <tr>
                  <th>UF</th>
                  <th className="is-number">NFs</th>
                  <th className="is-number">Com CTe</th>
                  <th className="is-number">Cobrado</th>
                  <th className="is-number">Extra</th>
                </tr>
              </thead>
              <tbody>
                {r.porUf.map((l) => (
                  <tr key={l.uf}>
                    <td>{l.uf}</td>
                    <td className="is-number">{fmtNumero(l.qtdNf)}</td>
                    <td className="is-number">{fmtNumero(l.qtdComparavel != null ? l.qtdComparavel : l.qtdNfComparavel)}</td>
                    <td className="is-number">{fmtBRL(n(l.vrFrete))}</td>
                    <td className="is-number">{fmtBRL(n(l.vrComplementar))}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
        <div className="lf-section">
          <div className="lf-section-title">Por transportadora atual</div>
          {(r.porTransportadora || []).length === 0 ? (
            <div className="lf-muted">Sem quebra por transportadora.</div>
          ) : (
            <Table className="bf-table" size="sm" responsive>
              <thead className="text-primary">
                <tr>
                  <th>Transportadora</th>
                  <th className="is-number">NFs</th>
                  <th className="is-number">Com CTe</th>
                  <th className="is-number">Cobrado</th>
                  <th className="is-number">Extra</th>
                </tr>
              </thead>
              <tbody>
                {r.porTransportadora.map((l) => (
                  <tr key={l.idTransportadora || l.nome}>
                    <td>{l.nome || `Transportadora ${l.idTransportadora}`}</td>
                    <td className="is-number">{fmtNumero(l.qtdNf)}</td>
                    <td className="is-number">{fmtNumero(l.qtdComparavel != null ? l.qtdComparavel : l.qtdNfComparavel)}</td>
                    <td className="is-number">{fmtBRL(n(l.vrFrete))}</td>
                    <td className="is-number">{fmtBRL(n(l.vrComplementar))}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </div>
    </>
  );
}
